import React, { useEffect, useState } from "react";
import { IRequirementViewModel } from "../../../interfaces/RequirementsInScopeModel";
import { FormDataFields } from "./form-data-fields";
import { evaluatePolicyEngine, evaluatePolicyEngineDataGroup, evaluatePolicyEngineSets, getRequirementsInScope } from '../../../services/fenergo-service';
import { useDispatch } from 'react-redux';
import { setSnackBar } from '../../../stores/ui/ui-action';
import { useEntityState } from "../../../stores/entity/entity.hooks";
import {
  getDynamicDataGroupFieldsReqPayload,
  getDynamicReqsPayload,
  getDefaultPropertyValue
} from "./form-helper-methods";
import { fetchDatagroups, fetchLookupLinks, fetchLookups, fetchUploadedDocuments } from "../../../stores/async/async-actions";
import { EntityDraftViewModel, Jurisdictions } from "../../../interfaces/EntityDraftViewModel";
import {
  setDynamicRequirementsLoadInProgress, setDynamicRequirements as setDynamicRequirementsAsync, setJurisdictions
} from "../../../stores/entity/entity-action";
import FENERGO_CONSTANTS from "../../../utils/fenergo-constants";
import CustomLoading from "../custom-loading";
import { useCodatState } from "../../../stores/ui/ui.hooks";
import { getUniqueJurisdictions } from "../../../utils/fenergo-utils";

interface FormDataProps {
  formMethods: any;
  watch?: any;
  initialValues?: any;
  entityDraft?: EntityDraftViewModel;
  journeyId: string;
  journeyName?: string;
  taskId?: string;
  entityType: string;
  categories: Array<string>;
  targetEntity: string;
  policyType: string;
  isCreateEntity?: boolean;
  readOnly: boolean;
  labelInsteadOfControl?: boolean;
  enableButton: any;
  jurisdictions?: any;
  controlledAccordian?: boolean;
  autoExpandCollapesPanels?: boolean;
  currentTaskName?: string;
}

export const FormData: React.FC<FormDataProps> = ({
  formMethods,
  currentTaskName,
  initialValues, entityDraft, journeyId, taskId, entityType, categories, targetEntity, policyType,
  journeyName,
  isCreateEntity, readOnly, labelInsteadOfControl, enableButton, jurisdictions,
  controlledAccordian = false, autoExpandCollapesPanels = false
}) => {
  const [initialRequirements, setInitialRequirements] = useState<IRequirementViewModel[]>(null);
  const [initialRequirementsLoadInProgress, setInitialRequirementsLoadInProgress] = useState<boolean>(false);
  const [loadingDynamicRequirements, setLoadingDynamicRequirements] = useState<boolean>(false);
  const [dynamicDataGroupRequirementsLoadInProgress, setDynamicDataGroupRequirementsLoadInProgress] = useState<boolean>(false);
  const [dynamicDataGroupRequirements, setDynamicDataGroupRequirements] = useState<IRequirementViewModel[]>(null);
  const [dynamicRequirements, setDynamicRequirements] = useState<IRequirementViewModel[]>(null);
  const [requirementsInScope, setRequirementsInScope] = useState<IRequirementViewModel[]>(null);
  const [filteredLookupList, setFilteredLookupList] = useState<Array<object>>([]);
  const [filteredLookupLinkList, setFilteredLookupLinkList] = useState<Array<object>>([]);
  const codatState = useCodatState();
  const dispatch = useDispatch();
  const {
    dataGroups, dataGroupsLoadInProgress,
    lookups, lookupsLoadInProgress,
    lookupLinks, lookupLinksLoadInProgress,
    documents, documentsLoadInProgress,
  } = useEntityState();

  const getJurisdictions = () => {
    return jurisdictions ? jurisdictions : entityDraft?.jurisdictions;
  }

  // Fetch Static Requirements
  const fetchInitialRequirements = async (
    entityType?: string, policyType?: string, targetEntity?: string, categories?: string[], jurisdictions?: Jurisdictions[]
  ) => {
    setInitialRequirementsLoadInProgress(true);
    dispatch(setDynamicRequirementsLoadInProgress(true));
    try {
      const requirements = await getRequirementsInScope(entityType, policyType, targetEntity, categories, jurisdictions);
      handleRequirementResponse(requirements);
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
    }
    setInitialRequirementsLoadInProgress(false);
    dispatch(setDynamicRequirementsLoadInProgress(false));
  }


  const handleRequirementResponse = (requirements) => {
    if (codatState?.companyId) {
      const reqIndex = requirements.findIndex(req => req.dataField?.propertyName === "uploadAccountingDocumentsManually");
      if (reqIndex > -1) {
        requirements.splice(reqIndex, 1);
      }
    }
    setInitialRequirements(requirements);
    setInitialRequirementsLoadInProgress(true);
  }

  // Fetch Dynamic Requirements
  const fetchDynamicRequirements = async (currentValues?: any) => {
    try {
      setDynamicRequirementsLoadInProgress(true);
      dispatch(setDynamicRequirementsLoadInProgress(true));

      //console.log("Current Value", currentValues);

      setLoadingDynamicRequirements(true);
      let dynamicReqsPayload = getDynamicReqsPayload(initialValues, currentValues, formMethods.getValues()) || {};
      if(codatState.companyId != null && codatState.platformName != null) {
        dynamicReqsPayload = {...dynamicReqsPayload , 
          platformName : {value: codatState.platformName, type: 'Single'},
          codatCompanyId: {value: codatState.companyId, type: 'Single'}
        }
      }
      if (Object.keys(dynamicReqsPayload).length === 0) {
        if (initialRequirements) {
          initialRequirements.forEach(req => {
            dynamicReqsPayload[req.dataField.propertyName] = getDefaultPropertyValue()
          })
        }
        else {
          setDynamicRequirements(null);
          setLoadingDynamicRequirements(false);
          dispatch(setDynamicRequirementsLoadInProgress(true));
          return;
        }
      }

      let dynamicReqs: IRequirementViewModel[] =
        await evaluatePolicyEngine(
          ['Data', 'Document'], categories, dynamicReqsPayload, getJurisdictions(), targetEntity
        )

      dynamicReqs = dynamicReqs
        .filter(req => req.entityType === entityType)
        .filter(req => req.targetEntity === targetEntity)

      setDynamicRequirements(dynamicReqs);
      dispatch(setDynamicRequirementsAsync(dynamicReqs));

      dispatch(setDynamicRequirementsLoadInProgress(false));
    } catch (error) {
      dispatch(setSnackBar({ show: true, message: 'Error while calling API', description: error.message, messageType: 'error' }));
      setLoadingDynamicRequirements(false);
      dispatch(setDynamicRequirementsLoadInProgress(true));
    }
  }

  // Fetch Dynamic Data Group Requirements
  const fetchDynamicDataGroupRequirements = async (currentValues) => {
    const dynamicDataGroupRequests = getDynamicDataGroupFieldsReqPayload(currentValues, dataGroups)

    if (dynamicDataGroupRequests.length > 0) {
      setDynamicDataGroupRequirementsLoadInProgress(true);
      const dynamicDataGroupResponses = await Promise.all(
        dynamicDataGroupRequests.map(req => evaluatePolicyEngineDataGroup(req.groupId, req.properties))
      );
      const dynamicDataGroups = dynamicDataGroupRequests.map((req, i) => {
        return { groupId: req.groupId, dataGroupFields: dynamicDataGroupResponses[i] }
      });
      setDynamicDataGroupRequirements(dynamicDataGroups);
      setDynamicDataGroupRequirementsLoadInProgress(false);
    } else {
      setDynamicDataGroupRequirements([]);
    }
  }

  // Filter Lookups for all Requirements
  const filterLookupsForReqsInScope = (reqsInScope) => {
    // Searching lookup IDs to get for Form Data Fields
    //console.log(reqsInScope)

    let lookupIds = reqsInScope
      .filter(field => (
        field.dataField?.propertyType === FENERGO_CONSTANTS.FIELDS.SELECT
        || field.dataField?.propertyType === FENERGO_CONSTANTS.FIELDS.MULTI_SELECT
      ) && field.dataField.propertyTypeId)
      .map(field => field.dataField.propertyTypeId)
      .filter((x, i, a) => a.indexOf(x) === i);

    // Searching lookup IDs to get for Form Data Groups
    let anyDataGroups = reqsInScope.filter((field) => field.dataField?.propertyType === FENERGO_CONSTANTS.FIELDS.DATA_GROUP)
    //console.log(anyDataGroups)
    //console.log(dataGroups)
    if (anyDataGroups.length > 0 && dataGroups) {
      let filteredDataGroups = dataGroups.filter(
        dataGroup => anyDataGroups.find(
          field => dataGroup && dataGroup["groupId"] === field.dataField.propertyTypeId && field.dataField.propertyTypeId
        )
      );

      //console.log(filteredDataGroups);
      if (filteredDataGroups)
        filteredDataGroups.forEach((dg) => {
          const lookups = dg.dataGroupFields
            .filter(field => (
              field.dataField.propertyType === FENERGO_CONSTANTS.FIELDS.SELECT
              || field.dataField.propertyType === FENERGO_CONSTANTS.FIELDS.MULTI_SELECT
            ) && field.dataField.propertyTypeId)
            .map(field => field.dataField.propertyTypeId)
            .filter((x, i, a) => a.indexOf(x) === i);

          lookupIds = lookupIds.concat(lookups);
        });
    }

    // Take only unique Lookup IDs
    lookupIds = lookupIds.filter(onlyUnique);

    let helperLoadingArray = [];

    lookupIds.forEach((lookupId) => {
      const filteredLookup = lookups.find((lookup: any) => {
        // console.log('LOOKUP IS', lookup);
        //TODO: Figure out why a lookup is undefined and remove below patch
        if (!lookup) {
          return false;
        }
        return lookup.lookupId === lookupId;
      });
      if (filteredLookup) helperLoadingArray.push(filteredLookup);
    });
    setFilteredLookupList(helperLoadingArray);
  }

  // Filter Lookup Links for all Requirements
  const filterLookupLinksForReqsInScope = (reqsInScope) => {
    // Searching lookup Link IDs to get for Form Data Fields
    let lookupLinkIds = reqsInScope
      .filter(field => (field.dataField?.propertyType === FENERGO_CONSTANTS.FIELDS.SELECT_LINK) && field.dataField.propertyTypeId)
      .map(field => field.dataField.propertyTypeId)
      .filter((x, i, a) => a.indexOf(x) === i);

    // Take only unique Lookup Link IDs
    lookupLinkIds = lookupLinkIds.filter(onlyUnique);

    let helperLoadingArray = [];

    lookupLinkIds.forEach((lookupLinkId) => {
      const filteredLookupLink = lookupLinks.find((lookupLink: any) => lookupLink.lookupLinkId === lookupLinkId);
      if (filteredLookupLink) helperLoadingArray.push(filteredLookupLink);
    });
    setFilteredLookupLinkList(helperLoadingArray);
  }

  useEffect(() => {
    enableButton(false);

    // Fetch Static Requirements
    if (!initialRequirementsLoadInProgress && !initialRequirements) {
      fetchInitialRequirements(entityType, policyType, targetEntity, categories, getJurisdictions());
    }

    // Fetch Dynamic Requirements
    if (!loadingDynamicRequirements && !dynamicRequirements) {
      fetchDynamicRequirements(initialValues);
    }

    // Fetch Uploaded Documents
    if (!documentsLoadInProgress && !documents) {
      dispatch(fetchUploadedDocuments(journeyId, entityDraft?.sourceEntityId));
    }

    // Fetch lookups, lookup links, data groups
    if (!lookupsLoadInProgress && !lookups
      && !lookupLinksLoadInProgress && !lookupLinks
      && !dataGroupsLoadInProgress && !dataGroups
    ) {
      dispatch(fetchLookups());
      dispatch(fetchLookupLinks());
      dispatch(fetchDatagroups());
    }

    // Fetch Dynamic Data Group Requirements
    if (!dynamicDataGroupRequirementsLoadInProgress && !dynamicDataGroupRequirements && initialValues && dataGroups) {
      fetchDynamicDataGroupRequirements(initialValues)
    }

    let reqsInScope;
    if (initialRequirements && dynamicRequirements && lookups && dataGroups) {
      // Combining static and dynamic requirements
      reqsInScope = initialRequirements.concat(
        dynamicRequirements.filter(req => !initialRequirements.find(
          eReq => eReq.dataField && req.dataField && (eReq.dataField.propertyName === req.dataField.propertyName)
        ))
      );
      // Sorting the requirements based on categories
      reqsInScope = reqsInScope.sort((req1, req2) => {
        return categories.indexOf(req1.category) - categories.indexOf(req2.category);
      });
      setRequirementsInScope(reqsInScope);

      enableButton(true);

      // Filter Lookups for all Requirements
      filterLookupsForReqsInScope(reqsInScope);

      // Filter Lookup Links for all Requirements
      filterLookupLinksForReqsInScope(reqsInScope);
    }
    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRequirements, dynamicRequirements, lookups, dataGroups]);

  const onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index;
  }
  const fetchDataGroupDisplayFields = (dataGroup: any, index: any) => {
    let result = initialValues[dataGroup][index] || {};
    let formMethodValue = formMethods.getValues()[dataGroup][index];
    if (typeof formMethodValue === 'object') {
      Object.keys(formMethodValue).map((propKey) => {
        result[propKey] = formMethodValue[propKey];
      });
    } else {
      result = formMethodValue;
    }
    return result;
  }
  const setInitialValues = () => {
    let result = initialValues;
    let formMethodValues = formMethods.getValues();
    formMethodValues && Object.keys(formMethodValues).map((propKey) => {
      let currentProp = formMethodValues[propKey]
      if (currentProp) {
        if (Array.isArray(currentProp) && !!result[propKey]) {
          Object.keys(currentProp).map((index) => {
            if (Array.isArray(result[propKey]))
              result[propKey][index] = fetchDataGroupDisplayFields(propKey, index);
            else
              result[propKey] = fetchDataGroupDisplayFields(propKey, index);
          });
        } else {
          result[propKey] = currentProp;
        }
      }
    });
    return result;
  }
  return (
    <>
      {(
        initialRequirementsLoadInProgress || dynamicDataGroupRequirementsLoadInProgress
        || dataGroupsLoadInProgress || lookupsLoadInProgress || lookupLinksLoadInProgress
      )
      }
      {(
        requirementsInScope && dynamicDataGroupRequirements
        && dataGroups && lookups && lookupLinks
        && (isCreateEntity || entityDraft)
      ) ? (
          <FormDataFields
            controlledAccordian={controlledAccordian}
            formMethods={formMethods}
            onChange={fetchDynamicRequirements}
            initialValues={setInitialValues()}
            documents={documents}
            journeyId={journeyId}
            journeyName={journeyName}
            taskId={taskId}
            entityId={entityDraft?.sourceEntityId}
            entityType={entityType}
            requirementsInScope={requirementsInScope}
            dynamicDataGroupRequirements={dynamicDataGroupRequirements}
            lookupList={filteredLookupList}
            lookupLinks={filteredLookupLinkList}
            lookups={lookups}
            dataGroupsSettings={dataGroups}
            loading={initialRequirementsLoadInProgress}
            readOnly={readOnly}
            labelInsteadOfControl={labelInsteadOfControl}
            currentTaskName={currentTaskName}
            autoExpandCollapesPanels={autoExpandCollapesPanels}
          />
        ) : ((currentTaskName === "Company") && <CustomLoading message="Loading" />)
      }
    </>
  );
};
